import {
  type CheckoutVariantStatus,
  type CheckoutVariant,
  type CheckoutVariantTarget,
} from "@fitness-app/data-models/entities/CheckoutVariant";

export type CheckoutVariantAvailability = "unlimited" | "scheduled" | "expired";

export interface CheckoutVariantFilters {
  search: string;
  target: CheckoutVariantTarget | null;
  status: CheckoutVariantStatus.Active | CheckoutVariantStatus.Inactive | null;
  availability: CheckoutVariantAvailability | null;
  onlyDefault: boolean;
}

export const emptyCheckoutVariantFilters: CheckoutVariantFilters = {
  search: "",
  target: null,
  status: null,
  availability: null,
  onlyDefault: false,
};

export const hasActiveCheckoutVariantFilters = (filters: CheckoutVariantFilters) =>
  Boolean(filters.search.trim() || filters.target || filters.status || filters.availability || filters.onlyDefault);

const getAvailability = (variant: CheckoutVariant, nowUnix: number): CheckoutVariantAvailability => {
  if (!variant.activeUntil) {
    return "unlimited";
  }

  return variant.activeUntil < nowUnix ? "expired" : "scheduled";
};

/** Search matches a fragment of the slug or the id, so "/6" and "-6" both find "sierpien2026-6". */
export const filterCheckoutVariants = (
  variants: CheckoutVariant[],
  filters: CheckoutVariantFilters,
  nowUnix = Math.floor(Date.now() / 1000),
): CheckoutVariant[] => {
  const search = filters.search.trim().toLowerCase().replace(/^\/+/, "");

  return variants.filter((variant) => {
    if (search && !variant.slug?.toLowerCase().includes(search) && !variant.id.toLowerCase().includes(search)) {
      return false;
    }

    if (filters.target && variant.target !== filters.target) {
      return false;
    }

    if (filters.status && variant.status !== filters.status) {
      return false;
    }

    if (filters.availability && getAvailability(variant, nowUnix) !== filters.availability) {
      return false;
    }

    if (filters.onlyDefault && !variant.default) {
      return false;
    }

    return true;
  });
};
